import React, { FC } from "react";

// Relative, not the "@shared/components" alias: jest resolves no path aliases, and this component's
// own test (PodiumTile.test.tsx) needs InitialsAvatar's REAL rendered initials, not a stubbed marker.
import { InitialsAvatar } from "../../../../../shared/components/InitialsAvatar";
// Same reason as InitialsAvatar above: relative, not "@assets", because jest resolves no path
// aliases. These are the real design assets pavel supplied (Figma `Leaderboard`, 9119:1089) --
// replacing the hand-drawn approximations this file used to build inline as `PodiumCrown`/
// `PodiumLaurel` below.
import { ReactComponent as PodiumCrownIcon } from "../../../../../assets/images/icons/spark/podium-crown.svg";
import { ReactComponent as PodiumLaurelIcon } from "../../../../../assets/images/icons/spark/podium-laurel.svg";
import { ReactComponent as SparkGlyphIcon } from "../../../../../assets/images/icons/spark/spark-glyph.svg";

import "./index.scss";

interface PodiumTileProps {
  place: 1 | 2 | 3;
  firstName: string;
  lastName: string;
  /** google-profile-pictures-plan.md Phase 2 step 3: the person's photo URL, if any -- threaded from
   *  the caller's own leaderboard row (`LeaderboardUser.avatar_url`). Falls back to the initials, same
   *  as `InitialsAvatar`'s own `imageUrl` prop. */
  imageUrl?: string | null;
  totalScore: number;
  // Renders "You" in place of firstName/lastName -- the same prop name, and the same word, as
  // `RankRow`'s own `isViewer`, so the podium marks its own viewer exactly as the rows do. The
  // avatar below always gets the real firstName/lastName (never this prop), so its initials stay the
  // person's own -- no background tint is added; the name swap is the whole treatment.
  isViewer?: boolean;
}

interface PodiumTreatment {
  avatarSize: 40 | 52;
  ring: string;
  plinth: string;
  plinthHeight: number;
  baseBar: string;
  crown: boolean;
  // Figma `Leaderboard` (9119:1089): only #1 sets its plinth numeral inside a white circular badge,
  // flanked by a laurel wreath -- #2/#3 keep the plain white numeral straight on the plinth. A prior
  // version drew a single open-arc laurel on ALL THREE plinths, which read as a rendering artefact and
  // was removed; this field is what stops that regression from coming back -- "the treatment is data,
  // not props" means a laurel for #2 or #3 can't be written down by a future caller.
  laurel: boolean;
}

// overview-leaderboard-card-plan.md Part 2, "Podium tiles -- the treatment is data, not props":
// `PodiumTile` takes `place: 1 | 2 | 3` and nothing else about its OWN appearance -- every visual
// difference between the three places (avatar size, ring/plinth colour, plinth height, base-bar
// colour, whether it has a crown, whether its numeral sits in a laurelled badge) is looked up from
// this single map instead of being a prop the caller could set inconsistently. A crownless winner, or
// a ring that doesn't match its plinth, cannot be written down. None of these hex values has a
// matching SASS variable yet (same "recorded, not fixed here" situation as FilterChips' own #EDE9FA),
// so they're hardcoded here alongside that existing precedent.
//
// `ring` and `plinth` are DELIBERATELY separate fields, not one colour reused for both, even though
// #2 and #3 happen to carry the identical hex for each (`#ADA8BD`/`#CA924E`). Place 1 proves they are
// independent: its ring (`#F1A52E`) is NOT its plinth colour (`#FFAF10`) -- so #2/#3's coincidence is
// not a signal to collapse the two fields into one.
const PODIUM: Record<1 | 2 | 3, PodiumTreatment> = {
  1: {
    avatarSize: 52,
    ring: "#F1A52E",
    plinth: "#FFAF10",
    plinthHeight: 72,
    baseBar: "#D79511",
    crown: true,
    laurel: true,
  },
  2: {
    avatarSize: 40,
    ring: "#ADA8BD",
    plinth: "#ADA8BD",
    plinthHeight: 58,
    baseBar: "#868296",
    crown: false,
    laurel: false,
  },
  3: {
    avatarSize: 40,
    ring: "#CA924E",
    plinth: "#CA924E",
    plinthHeight: 45,
    baseBar: "#84623A",
    crown: false,
    laurel: false,
  },
};

// Podium tiles render in this exact left-to-right order (#2, #1, #3) -- the tallest plinth sits in the
// middle, which is what actually reads as a podium (overview-leaderboard-card-plan.md §"web tests":
// "the tallest plinth moves left and it stops reading as a podium" is the named mutation this order
// guards against). Exported alongside PodiumTile itself (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L1)
// so every caller shares the SAME order rather than each keeping its own copy of this literal that
// could silently drift -- OverviewLeaderboard.tsx and LeaderboardsContainer.tsx both import this.
export const PODIUM_ORDER: (1 | 2 | 3)[] = [2, 1, 3];

/**
 * One podium position (Figma `Leaderboard`, 9119:1089). `place` is the only prop that governs its own
 * appearance -- avatar size, ring/plinth colour, plinth height, base-bar colour, and whether it shows
 * a crown all come from the `PODIUM` map above, keyed on this same value. `firstName`/`lastName`/
 * `totalScore` are content, not appearance, so they stay plain props.
 *
 * Moved out of `components/MySparks/OverviewLeaderboard/PodiumTile` to
 * `components/Leaderboard/PodiumTile` (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L1) once the
 * Leaderboard tab became a second consumer. It sits in the Leaderboard folder, NOT in
 * `shared/components`: it takes only primitives, but a podium tile is a leaderboard concept, and both
 * consumers live under `containers/Recognition/` -- the same shelf `LeaderboardBoardToggle`,
 * `LeaderboardLoadStatus` and `leaderboardEmptyCopy` are already shared from, MySparks reaching into
 * it exactly as it does for those three.
 *
 * @param props.place - this tile's podium position; also the `PODIUM` lookup key.
 * @param props.firstName - the winner's first name.
 * @param props.lastName - the winner's last name.
 * @param props.imageUrl - the winner's photo URL, if any (see this prop's own interface comment).
 * @param props.totalScore - the winner's total score for the board/period this podium renders.
 * @param props.isViewer - true when this tile is the logged-in viewer's own; renders "You" instead
 *   of firstName/lastName. The avatar still uses the real firstName/lastName, so its initials stay
 *   the person's own.
 */
const PodiumTile: FC<PodiumTileProps> = (props) => {
  const { place, firstName, lastName, imageUrl, totalScore, isViewer } = props;
  const treatment = PODIUM[place];
  const displayName = isViewer ? "You" : `${firstName} ${lastName}`;

  return (
    <div className="podium-tile" data-testid={`podium-tile-${place}`}>
      {/* `podium-tile-avatar-group` carries the crown at zero gap so its own `margin-bottom: -2px`
          (below) is the ONLY spacing between the crown and the avatar ring -- the group's inner
          `podium-tile-avatar-name` is what applies the real 6px gap, between the ring and the name. */}
      <div className="podium-tile-avatar-group">
        {treatment.crown ? (
          <PodiumCrownIcon
            className="podium-tile-crown-icon"
            aria-hidden="true"
            focusable="false"
            data-testid="podium-crown"
          />
        ) : null}
        <div className="podium-tile-avatar-name">
          <div className="podium-tile-avatar-ring" style={{ borderColor: treatment.ring }}>
            <InitialsAvatar firstName={firstName} lastName={lastName} size={treatment.avatarSize} imageUrl={imageUrl} />
          </div>
          {/* Name and score are ONE block at zero gap -- the frame stacks them tight
              (`I9396:2248;9489:4865`), and the 6px on `podium-tile-avatar-name` is only ever between the
              avatar and this block. The score used to be a direct child of the tile, which handed it the
              tile's own 16px gap instead: measured 16px between the name's bottom and the score's top on
              all three tiles, against the frame's 0. */}
          <div className="podium-tile-name-score">
            <div className="podium-tile-name" title={displayName}>
              {displayName}
            </div>
            <div className="podium-tile-score">
              <SparkGlyphIcon className="podium-tile-score-glyph" aria-hidden="true" focusable="false" />
              <span>{totalScore}</span>
            </div>
          </div>
        </div>
      </div>
      {/* The plinth and its base bar are ONE group at zero gap (`I9396:2248;9501:5744`) -- the base bar
          reads as the plinth's own foot, not as a separate slab. They used to be two direct children of
          the tile, which handed each of them the tile's own 16px gap: measured 16px of white between the
          plinth's bottom edge and the base bar, against the frame's 0. Same defect, same cause, as the
          name/score pair above. */}
      <div className="podium-tile-plinth-group">
        <div className="podium-tile-plinth-wrap">
          <div
            className="podium-tile-plinth"
            style={{ backgroundColor: treatment.plinth, height: treatment.plinthHeight }}
            data-testid={`podium-plinth-${place}`}
          >
            {/* The rank disc renders for every place now (Figma's own "shared by all three tiles"
              section) -- only #1 (`treatment.laurel`) gets the `--badge` modifier, which pulls it up
              with a negative margin so the laurel that follows visually wraps around it rather than
              sitting underneath. #2/#3 render the same disc with no laurel after it. */}
            <span className={treatment.laurel ? "podium-tile-place podium-tile-place--badge" : "podium-tile-place"}>
              {place}
            </span>
            {treatment.laurel ? (
              <PodiumLaurelIcon
                className="podium-tile-laurel-icon"
                aria-hidden="true"
                focusable="false"
                data-testid="podium-laurel"
              />
            ) : null}
          </div>
        </div>
        <div className="podium-tile-base" style={{ backgroundColor: treatment.baseBar }} />
      </div>
    </div>
  );
};

export default PodiumTile;
